import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { HELP_SECTIONS, helpTargetForPath } from './helpContent.js';
import './help.scss';

function HelpNav({ sections, activeId, onSelect }) {
  return (
    <div className="help-nav" role="tablist">
      {sections.map((s) => (
        <button
          key={s.id}
          type="button"
          role="tab"
          aria-selected={s.id === activeId}
          className={`help-nav-tab${s.id === activeId ? ' active' : ''}`}
          onClick={() => onSelect(s.id)}
        >
          <i className={s.icon} aria-hidden="true" /> <span>{s.title}</span>
        </button>
      ))}
    </div>
  );
}

function HelpContent({ section, topicId, onSelectTopic }) {
  const topic = section.topics.find((t) => t.id === topicId) || section.topics[0];
  return (
    <>
      <div className="help-pills">
        {section.topics.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`help-pill${t.id === topic.id ? ' active' : ''}`}
            onClick={() => onSelectTopic(t.id)}
          >
            {t.title}
          </button>
        ))}
      </div>
      <div className="help-body" dangerouslySetInnerHTML={{ __html: topic.html }} />
    </>
  );
}

/** "?" button + slide-in help drawer. Rendered once from the shared layout shell. */
export default function HelpDrawer({ floating = false }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [sectionId, setSectionId] = useState('welcome');
  const [topicId, setTopicId] = useState('getting-started');

  const openDrawer = () => {
    const target = helpTargetForPath(pathname);
    setSectionId(target.sectionId);
    setTopicId(target.topicId);
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  const section = HELP_SECTIONS.find((s) => s.id === sectionId) || HELP_SECTIONS[0];

  return (
    <>
      <button
        type="button"
        className={floating ? 'help-fab' : 'btn btn-sm btn-outline-light me-2'}
        onClick={openDrawer}
        aria-label="Open help"
        title="Help"
      >
        <i className="fas fa-question" aria-hidden="true" />
      </button>
      {open && (
        <>
          <div
            className="help-backdrop"
            role="presentation"
            onClick={() => setOpen(false)}
            onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
          />
          <aside className="help-drawer" role="dialog" aria-label="Help">
            <div className="help-header">
              <h2 className="h5 mb-0">Help</h2>
              <button type="button" className="btn-close" aria-label="Close help" onClick={() => setOpen(false)} />
            </div>
            <HelpNav
              sections={HELP_SECTIONS}
              activeId={section.id}
              onSelect={(id) => {
                setSectionId(id);
                setTopicId(HELP_SECTIONS.find((s) => s.id === id).topics[0].id);
              }}
            />
            <HelpContent section={section} topicId={topicId} onSelectTopic={setTopicId} />
          </aside>
        </>
      )}
    </>
  );
}
